import {
  Badge,
  Button,
  Flex,
  Text,
  Tooltip,
} from '@retracedgmbh/ui-components-lib';
import useTranslator from '@/hooks/useTranslator';
import type {
  AiProcessingItem,
  AiProcessingStats,
} from '@/services/api/types/ai-processing';
import ActiveJobsPopover from './ActiveJobsPopover';
import { isItemReviewable } from './auditStages';
import { AI_PROCESSING_TRIGGER_ID } from './flyToAiProcessing';

interface AIProcessingStatProps {
  onOpen: () => void;
  stats: AiProcessingStats | undefined;
  items: AiProcessingItem[];
  classifiedCount: number;
}

export function AIProcessingStat({
  onOpen,
  stats,
  items,
  classifiedCount,
}: AIProcessingStatProps) {
  const { getTranslation } = useTranslator({ page: 'ai-processing' });

  const percentage = stats?.percentage ?? 0;
  const isProcessing = (stats?.processing ?? 0) > 0;
  const inProgressItems = items.filter(item => item.status === 'PROCESSING');
  // "to review" badge: count only jobs that still have something to review — a
  // finished, not-yet-reviewed reviewable stage (isItemReviewable) — NOT every
  // finished job. `stats.completed` counts all COMPLETED jobs (including ones the
  // user has already reviewed), so it over-counts the badge; derive from the
  // items instead, matching the queue's "Ready to review" section.
  const readyItems = items.filter(isItemReviewable);
  const reviewCount = readyItems.length;

  const button = (
    <Button id={AI_PROCESSING_TRIGGER_ID} onClick={onOpen}>
      <Flex gap='sm' alignItems='center'>
        <span className='tw:relative'>
          <i className='fa-regular fa-sparkles' />
          {classifiedCount > 0 && (
            <span className='tw:absolute tw:-top-2 tw:-right-2 tw:flex tw:items-center tw:justify-center tw:min-w-4 tw:h-4 tw:rounded-full tw:bg-content-info-ai-bold tw:px-0.5'>
              <Text
                as='span'
                className='tw:text-[10px] tw:leading-none tw:text-white tw:font-semibold'
              >
                {classifiedCount}
              </Text>
            </span>
          )}
        </span>
        <Text>{getTranslation('stat.title')}</Text>
        {/* Ring shows the overall progress of all running jobs. It hides at
            100% (no jobs left processing); the "to review" badge then carries
            over the completed-but-unreviewed jobs. Both can show at once when
            one job is still running while another is ready to review. */}
        {isProcessing && <ProgressRing percentage={percentage} />}
        {reviewCount > 0 && (
          <Badge type='warning' radius='squared'>
            <Text className='tw:text-amber-800'>
              {getTranslation('stat.toReview', { count: reviewCount })}
            </Text>
          </Badge>
        )}
      </Flex>
    </Button>
  );

  // While jobs are running, hovering reveals a rich progress popover. With no
  // running job we show the plain empty-state tooltip — even if items await
  // review (the badge already surfaces those).
  if (isProcessing) {
    return (
      <ActiveJobsPopover
        jobsTotal={stats?.total ?? 0}
        runningCount={stats?.processing ?? 0}
        readyCount={reviewCount}
        inProgressItems={inProgressItems}
        readyItems={readyItems}
        onOpenQueue={onOpen}
      >
        {button}
      </ActiveJobsPopover>
    );
  }

  return (
    <Tooltip title={getTranslation('stat.tooltipEmpty')} placement='bottom'>
      {button}
    </Tooltip>
  );
}

// Compact circular progress for the navbar button. The lib's
// `CircularProgressBar` hardcodes a blue path color and exposes no size prop,
// so it can't render this design's small dark-slate ring — a minimal inline
// SVG (sized + colored via semantic tokens) is used instead.
// Original AC was 22 / 2.5 (too small) and 44 / 5 (too big); 30 / 3.5 is the
// middle ground. NOTE: keep the svg's `tw:size-[30px]` class in sync with
// RING_SIZE (Tailwind needs a literal class, see the svg comment below).
const RING_SIZE = 30;
const RING_STROKE = 3.5;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

function ProgressRing({ percentage }: { percentage: number }) {
  const clamped = Math.min(100, Math.max(0, percentage));
  // Never exceed a full 360° arc — dashoffset is clamped via the 0–100 pct.
  const dashOffset = RING_CIRCUMFERENCE * (1 - clamped / 100);

  return (
    <Flex
      alignItems='center'
      justify='center'
      position='relative'
      display='inline-flex'
    >
      {/* `tw:size-[30px]` is required: the lib Button force-sizes any inner svg
          WITHOUT a `size-*` class to 16px (`[&_svg:not([class*=size-])]:size-4`),
          which was clamping the ring. The `size-` class opts out + sets the size
          (keep this literal in sync with RING_SIZE). */}
      <svg
        width={RING_SIZE}
        height={RING_SIZE}
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
        className='tw:size-[30px] tw:-rotate-90'
        aria-hidden='true'
      >
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          fill='none'
          strokeWidth={RING_STROKE}
          strokeOpacity={0.18}
          className='tw:stroke-content-primary'
        />
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          fill='none'
          strokeWidth={RING_STROKE}
          strokeLinecap='round'
          strokeDasharray={RING_CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
          className='tw:stroke-content-primary tw:transition-[stroke-dashoffset] tw:duration-300 tw:ease-linear'
        />
      </svg>
      <Text
        as='span'
        weight='semibold'
        className='tw:absolute tw:text-xs tw:leading-none'
      >
        {clamped}%
      </Text>
    </Flex>
  );
}
